import { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import _ from "lodash";
import {
  useRealtimeTags,
  useRealtimeUnits,
  useRealtimeDrivers,
  useRealtimeAssets
} from 'Core/hooks';
import { clientQuery } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';

const TAG_ID_ALL_TAGS = -99;
const TAG_UNTAGGED = {
  "id": -1,
  "label": "untagged",
  "level": 0,
  "tagPath": "-1",
  "disabled": false,
}

const SEARCH_UNITS_QUERY = `query searchUnits ($conditions: [UnitSearchInput]) {
    cameras: searchUnits (conditions: $conditions) {
        items {
            id
            label
            esn
            deviceModel {
                name
                manufacturer {
                    name
                }
            }
        }
        total
    }
}`;

export const useTagsManage = ({ onlyTags, videoUnits = false, deviceModelSelected = null }) => {

  // Phase 4: Use specialized O(1) Realtime Hooks targeting structural group changes
  // We only track the `tags` property to prevent re-renders when unrelated properties (like speed) mutate.
  const drivers = useRealtimeDrivers({ trackFields: ['tags'] });
  const realtimeUnits = useRealtimeUnits({ trackFields: ['tags'] });
  const assets = useRealtimeAssets({ trackFields: ['tags'] });

  const tags = useRealtimeTags({
    trackFields: ['label', 'disabled', 'tagPath']
  });

  const forms = useSelector((state) => state.catalogsRedux.forms);
  const subusers = useSelector((state) => state.catalogsRedux.subusers);
  const landmarks = useSelector(
    (state) => state.catalogsRealtimeRedux.landmarks
  );
  const geofences = useSelector(
    (state) => state.catalogsRealtimeRedux.geofences
  );
  const loadingDrivers = useSelector(
    (state) => state.catalogsRealtimeRedux.loadingDrivers
  );
  const loadingUnits = useSelector(
    (state) => state.catalogsRealtimeRedux.loading
  );
  const loadingTags = useSelector((state) => state.catalogsRedux.loadingTags);
  const loadingForms = useSelector((state) => state.catalogsRedux.loadingForms);
  const loadingAssets = useSelector(
    (state) => state.catalogsRedux.loadingAssets
  );
  const loadingSubusers = useSelector(
    (state) => state.catalogsRedux.loadingSubusers
  );
  const loadingLandmarks = useSelector(
    (state) => state.catalogsRealtimeRedux.loadingLandmarks
  );
  const loadingGeofences = useSelector(
    (state) => state.catalogsRealtimeRedux.loadingGeofences
  );
  const user = useSelector((state) => state.securityRedux.user);

  const [videoDevicesItems, setVideoDevicesItems] = useState([]);
  const [loadingVideoDevices, setLoadingVideoDevices] = useState(false);

  useEffect(() => {
    if (!videoUnits) {
      setVideoDevicesItems([]);
      return;
    }
    let cancelled = false;
    setLoadingVideoDevices(true);
    clientQuery(
      SEARCH_UNITS_QUERY,
      {
        conditions: [
          {
            fields: ["deviceModel_manufacturer_name"],
            terms: ["VidFleet", "SmartWitness"],
            exact: true,
          },
          {
            fields: ["linkedMode"],
            terms: ["1", "0"],
            exact: true,
          },
        ],
      },
      endpoints.GRAPHQL_GENERAL_V2,
    )
      .then((data) => {
        if (cancelled || !data?.cameras?.items) return;
        let items = data.cameras.items;
        const deviceModelId = deviceModelSelected?.id;
        if (deviceModelId && items?.length) {
          items = items.filter(
            (item) => String(item?.deviceModel?.name) === String(deviceModelId),
          );
        }
        setVideoDevicesItems(items);
      })
      .catch(() => {
        if (!cancelled) setVideoDevicesItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingVideoDevices(false);
      });
    return () => { cancelled = true; };
  }, [videoUnits, deviceModelSelected?.id]);

  const unitsFromVideoDevices = useMemo(() => {
    if (!videoUnits || !videoDevicesItems?.length) return [];
    return videoDevicesItems
      .map((item) => {
        const id = item?.id;
        const label = item?.label ?? item?.esn ?? '';
        if (!id) return null;
        const realtimeUnit = realtimeUnits?.find((ru) => String(ru?.id) === String(id));
        const tags = realtimeUnit?.tags?.length ? realtimeUnit.tags : [TAG_UNTAGGED];
        return { id, label, tags };
      })
      .filter(Boolean);
  }, [videoUnits, videoDevicesItems, realtimeUnits]);

  const units = useMemo(() => {
    const baseUnits = videoUnits ? unitsFromVideoDevices : (realtimeUnits ?? []);
    return baseUnits?.map((x) => ({
      ...x,
      tags: !x?.tags?.length ? [TAG_UNTAGGED] : x.tags,
    }));
  }, [videoUnits, unitsFromVideoDevices, realtimeUnits]);

  // loading logic (add new validations if necessary)
  let isLoading;
  if (onlyTags) {
    isLoading = videoUnits ? (loadingVideoDevices || loadingTags) : loadingTags;
  } else if (videoUnits) {
    isLoading = loadingVideoDevices || loadingTags;
  } else {
    isLoading =
      loadingUnits ||
      loadingDrivers ||
      loadingLandmarks ||
      loadingGeofences ||
      loadingSubusers ||
      loadingForms ||
      loadingAssets ||
      loadingTags;
  }

  const isSubUser = user?.isSubUser;
  const tagIds = user?.tagIds && JSON.parse(user?.tagIds) || [];
  const allPermissionsTags = tagIds?.includes(TAG_ID_ALL_TAGS) ? true : false;

  const tagsWithPermission = useMemo(() => {
    const filteredTags = tags?.filter((tag) => tag?.id !== TAG_ID_ALL_TAGS);

    if (tagIds?.includes(TAG_ID_ALL_TAGS)) {
      return filteredTags;
    }

    let tagWithPermission = filteredTags?.filter((tag) => tag?.disabled === false);

    let tagsIdsToShow = tagWithPermission?.map((tag) => tag?.tagPath?.split('/'))?.flat();

    tagsIdsToShow = _.uniq(tagsIdsToShow);

    let tagsToShow = tags?.filter((tag) => tagsIdsToShow?.includes(tag.id?.toString()));

    return tagsToShow;
  }, [tags]);

  const sortedTags = useMemo(() => tagsWithPermission?.length ? [...tagsWithPermission].sort((a, b) => {
    if (a.id === -1) return -1;
    if (b.id === -1) return 1;
    return 0;
  }) : tagsWithPermission, [tagsWithPermission]);

  return {
    drivers: drivers?.map(x => ({ ...x, tags: (!x?.tags || x?.tags?.length === 0) ? [TAG_UNTAGGED] : x?.tags })),
    units,
    tags: sortedTags,
    forms: forms?.map(x => ({ ...x, tags: (!x?.tags || x?.tags?.length === 0) ? [TAG_UNTAGGED] : x?.tags })),
    assets: assets?.map(x => ({ ...x, tags: (!x?.tags || x?.tags?.length === 0) ? [TAG_UNTAGGED] : x?.tags })),
    subusers: subusers?.map(x => ({ ...x, tags: (!x?.tags || x?.tags?.length === 0) ? [TAG_UNTAGGED] : x?.tags })),
    landmarks: landmarks?.map(x => ({ ...x, tags: (!x?.tags || x?.tags?.length === 0) ? [TAG_UNTAGGED] : x?.tags })),
    geofences: geofences?.map(x => ({ ...x, tags: (!x?.tags || x?.tags?.length === 0) ? [TAG_UNTAGGED] : x?.tags })),
    isLoading,
    isSubUser,
    tagIds,
    user,
    allPermissionsTags
  };
};
